/**
 * DataMap -- "how your data connects": every table on a ring grouped by area, the keys
 * tables share inside it, verified links in deep amber, and a route finder between any two
 * tables. One component for the admin Context Graph page and the customer portal's Data map
 * tab; it draws the payload of services/context_graph_map.py and fetches nothing itself.
 *
 *   audience="admin"   raw table names, the detector's wording ("found by matching values")
 *   audience="portal"  friendly table names, customer wording ("link found in your data")
 *
 * The layout is computed once per payload (layout.js); the link filter and hover/click
 * focus only restyle what is already drawn.
 */
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import * as d3 from 'd3'
import LoadError from '../LoadError'
import {
  computeLayout, visibility, reachOf, findRoute, reversePath, pt,
  R, DOMAIN_COLORS, AMBER, INK, MUTED, FAINT, HOLLOW,
} from './layout'
import './DataMap.css'

const WORDS = {
  admin: {
    verified: 'Verified',
    matched: 'Found by matching values',
    verifiedOne: 'verified join',
    matchedOne: 'found by matching values',
    notBuilt: 'No join map has been built for this customer yet. Rebuild the graph to draw one.',
    noTables: 'No tables are loaded for this customer yet, so there is nothing to map.',
    stale: 'out of date',
    staleTitle: 'Data has loaded since the join map was built',
    mapWord: 'Join map',
  },
  portal: {
    verified: 'Verified links',
    matched: 'Links found in your data',
    verifiedOne: 'verified link',
    matchedOne: 'link found in your data',
    notBuilt: "Your data map isn't ready yet. It is drawn after your data has loaded.",
    noTables: 'No data has been loaded yet, so there is nothing to map.',
    stale: 'older than your latest data',
    staleTitle: 'Your data has loaded since this map was drawn',
    mapWord: 'Map',
  },
}

function fmtDate(iso) {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}

// "Employee" -> "Employee key"; a key already named so ("Company key") stays as it is
function keyName(label) {
  return /\bkey$/i.test(label) ? label : `${label} key`
}

function clip(s, n) {
  return s.length > n ? `${s.slice(0, n - 1)}…` : s
}

function possessive(name) {
  if (!name) return null
  return /s$/i.test(name) ? `${name}’` : `${name}’s`
}

// The tooltip follows the pointer, so it is moved through the DOM rather than state.
function moveTip(tip, e) {
  if (!tip || tip.hidden) return
  const r = tip.getBoundingClientRect()
  let x = e.clientX + 14, y = e.clientY + 14
  if (x + r.width > window.innerWidth - 8) x = e.clientX - r.width - 14
  if (y + r.height > window.innerHeight - 8) y = e.clientY - r.height - 14
  tip.style.left = `${Math.max(8, x)}px`
  tip.style.top = `${Math.max(8, y)}px`
}

function showTip(tip, e, title, lines) {
  if (!tip) return
  tip.replaceChildren()
  const b = document.createElement('b')
  b.textContent = title
  tip.append(b)
  lines.filter(Boolean).forEach(l => { tip.append(document.createElement('br')); tip.append(l) })
  tip.hidden = false
  moveTip(tip, e)
}

function Marker({ color, dashed }) {
  return (
    <svg viewBox="0 0 14 8" className="dmap-mk" aria-hidden="true">
      <line x1="1" x2="13" y1="4" y2="4" stroke={color} strokeWidth={dashed ? 1.8 : 2.4}
        strokeLinecap="round" strokeDasharray={dashed ? '3.5 2.5' : undefined} />
    </svg>
  )
}

function Row({ color, dashed, name, meta, note, noteVerified, onClick }) {
  return (
    <li>
      <Marker color={color} dashed={dashed} />
      <span>
        {onClick ? <button type="button" className="dmap-linkbtn" onClick={onClick}>{name}</button> : name}
        {note && <small className={noteVerified ? 'is-verified' : undefined}>{note}</small>}
      </span>
      <span className="dmap-m">{meta}</span>
    </li>
  )
}

export default function DataMap({ data, loading = false, error = null, onRetry, audience = 'admin', subject, onSelectTable }) {
  const W = WORDS[audience] || WORDS.admin

  if (error) {
    return (
      <section className="dmap dmap--state">
        <LoadError what="the data map" error={error} onRetry={onRetry} />
      </section>
    )
  }
  if (loading || !data) {
    return (
      <section className="dmap dmap--state" aria-busy="true">
        <div className="dmap-state"><div className="dmap-spinner" /><p>Loading the data map…</p></div>
      </section>
    )
  }
  if (!data.exists || !(data.tables?.length || data.settings_tables?.length)) {
    return (
      <section className="dmap dmap--state">
        <div className="dmap-state dmap-state--empty">
          <p className="dmap-state-title">Data map</p>
          <p>{data.exists ? W.noTables : W.notBuilt}</p>
          {onRetry && <button type="button" className="dmap-btn" onClick={onRetry}>Check again</button>}
        </div>
      </section>
    )
  }
  return <DataMapView data={data} audience={audience} subject={subject} W={W} onSelectTable={onSelectTable} />
}

function DataMapView({ data, audience, subject, W, onSelectTable }) {
  const portal = audience === 'portal'
  const layout = useMemo(() => computeLayout(data), [data])
  const [filter, setFilter] = useState({ verified: true, matched: true })
  const vis = useMemo(() => visibility(layout, filter), [layout, filter])
  const [showLabels, setShowLabels] = useState(() => !(typeof window !== 'undefined'
    && window.matchMedia && window.matchMedia('(max-width: 640px)').matches))
  const [hover, setHover] = useState(null)
  const [pinned, setPinned] = useState(null)
  const [routeReq, setRouteReq] = useState(null)
  const svgRef = useRef(null)
  const tipRef = useRef(null)
  const sel = useRef(null)
  const reduce = useMemo(() => typeof window !== 'undefined' && window.matchMedia
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches, [])

  const nameOf = useCallback((id) => {
    const t = layout.byId.get(id) || layout.settings.find(s => s.id === id)
    return t ? (portal ? (t.label || t.id) : t.id) : id
  }, [layout, portal])
  const hubLabel = useCallback((id) => keyName(layout.hubById.get(id)?.label || id), [layout])

  const route = useMemo(() => {
    if (!routeReq) return null
    return { ...routeReq, result: findRoute(vis, routeReq.from, routeReq.to) }
  }, [routeReq, vis])

  const focus = pinned || hover

  const tipShow = (e, title, lines) => showTip(tipRef.current, e, title, lines)
  const tipHide = () => { if (tipRef.current) tipRef.current.hidden = true }

  // handlers read the latest closures through a ref, so the drawing is never redone for them
  const handlers = useRef({})
  handlers.current = {
    enter: (e, kind, obj) => {
      setHover({ type: kind, id: obj.key || obj.id })
      if (kind === 'table') {
        tipShow(e, nameOf(obj.id), [`${data.domains?.find(d => d.id === obj.domain)?.label || obj.domain} · ${vis.connected.has(obj.id) ? 'linked' : 'no link yet'}`])
      } else if (kind === 'hub') {
        tipShow(e, keyName(obj.label), [`${vis.hubCount.get(obj.id) || 0} tables`])
      } else if (kind === 'pair') {
        tipShow(e, `${nameOf(obj.a)} ↔ ${nameOf(obj.b)}`, obj.cols.slice(0, 3).map(c =>
          `${c.ac} = ${c.bc}`).concat(obj.cols.length > 3 ? [`+${obj.cols.length - 3} more`] : []))
      } else if (kind === 'verified') {
        tipShow(e, `${nameOf(obj.a)} ↔ ${nameOf(obj.b)}`, [W.verifiedOne])
      }
    },
    leave: () => { tipHide(); setHover(null) },
    pin: (kind, obj) => {
      setPinned({ type: kind, id: obj.key || obj.id })
      if (kind === 'table' && onSelectTable) onSelectTable(obj)
    },
  }

  // ── draw once per layout ──
  useEffect(() => {
    const svgEl = svgRef.current
    if (!svgEl) return
    const svg = d3.select(svgEl)
    svg.selectAll('*').remove()
    const h = () => handlers.current
    const tipMove = (e) => moveTip(tipRef.current, e)

    const arcG = svg.append('g').attr('class', 'dmap-arcs')
    layout.arcs.forEach(a => {
      arcG.append('path').attr('d', a.d).attr('fill', a.color)
      arcG.append('path').attr('id', a.key).attr('fill', 'none').attr('d', a.textPath)
      if (a.fits) {
        arcG.append('text').attr('fill', a.color).attr('class', 'dmap-arc-label').attr('font-size', a.fontSize)
          .append('textPath').attr('href', `#${a.key}`).attr('startOffset', '50%').attr('text-anchor', 'middle')
          .text(a.label.toUpperCase())
      }
    })
    svg.append('circle').attr('r', R - 6).attr('fill', 'none').attr('stroke', '#EFEBE3').attr('stroke-width', 1)

    const memG = svg.append('g').attr('fill', 'none').attr('class', 'dmap-layer dmap-fade')
    const mem = memG.selectAll('path').data(layout.members).enter().append('path')
      .attr('d', m => m.d).attr('stroke', m => DOMAIN_COLORS[layout.byId.get(m.t).domain] || DOMAIN_COLORS.other)
      .attr('stroke-linecap', 'round')

    const pairG = svg.append('g').attr('fill', 'none').attr('class', 'dmap-layer dmap-fade')
    const pair = pairG.selectAll('path.dmap-pair').data(layout.pairs).enter().append('path').attr('class', 'dmap-pair')
      .attr('d', p => p.d).attr('stroke', p => DOMAIN_COLORS[layout.byId.get(p.a).domain] || DOMAIN_COLORS.other)
      .attr('stroke-dasharray', '5 3.5').attr('stroke-linecap', 'round')
    const pairHit = pairG.selectAll('path.dmap-hit').data(layout.pairs).enter().append('path').attr('class', 'dmap-hit')
      .attr('d', p => p.d).attr('stroke', 'transparent').attr('stroke-width', 9).style('cursor', 'pointer')
      .on('pointerenter', (e, p) => h().enter(e, 'pair', p)).on('pointermove', tipMove).on('pointerleave', () => h().leave())
      .on('click', (e, p) => { e.stopPropagation(); h().pin('pair', p) })

    const verG = svg.append('g').attr('fill', 'none').attr('class', 'dmap-layer')
    verG.selectAll('path.dmap-vcase').data(layout.verified).enter().append('path').attr('class', 'dmap-vcase')
      .attr('d', v => v.d).attr('stroke', '#FFFFFF').attr('stroke-width', 6)
    verG.selectAll('path.dmap-v').data(layout.verified).enter().append('path').attr('class', 'dmap-v')
      .attr('d', v => v.d).attr('stroke', AMBER).attr('stroke-width', 2.6).attr('stroke-linecap', 'round')
      .style('cursor', 'pointer')
      .on('pointerenter', (e, v) => h().enter(e, 'verified', v)).on('pointermove', tipMove).on('pointerleave', () => h().leave())
      .on('click', (e, v) => { e.stopPropagation(); h().pin('verified', v) })

    const hiG = svg.append('g').attr('fill', 'none').attr('pointer-events', 'none')
    const tG = svg.append('g')
    const tn = tG.selectAll('g').data(layout.tables).enter().append('g').attr('class', 'dmap-t')
      .attr('tabindex', 0).attr('role', 'button')
      .attr('aria-label', t => `${nameOf(t.id)}, ${t.domain}`)
      .on('pointerenter', (e, t) => h().enter(e, 'table', t)).on('pointermove', tipMove).on('pointerleave', () => h().leave())
      .on('click', (e, t) => { e.stopPropagation(); h().pin('table', t) })
      .on('keydown', (e, t) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); h().pin('table', t) } })
    const dots = tn.append('circle').attr('cx', t => t.p[0]).attr('cy', t => t.p[1]).attr('r', 4.3).attr('stroke-width', 1.6)
    const labels = tn.append('text').attr('class', 'dmap-tlabel')
      .text(t => clip(nameOf(t.id), 32)).attr('font-size', layout.labelSize)
      .attr('transform', t => {
        const deg = (t.a * 180) / Math.PI - 90
        const flip = t.a > Math.PI
        const p = pt(t.a, R + 34)
        return `translate(${p[0].toFixed(1)},${p[1].toFixed(1)}) rotate(${(flip ? deg + 180 : deg).toFixed(2)})`
      })
      .attr('text-anchor', t => (t.a > Math.PI ? 'end' : 'start')).attr('dy', '0.32em')

    const hG = svg.append('g')
    const hn = hG.selectAll('g').data(layout.hubs).enter().append('g').attr('class', 'dmap-h')
      .attr('transform', x => `translate(${x.x.toFixed(1)},${x.y.toFixed(1)})`)
      .attr('tabindex', 0).attr('role', 'button').attr('aria-label', x => keyName(x.label))
      .on('pointerenter', (e, x) => h().enter(e, 'hub', x)).on('pointermove', tipMove).on('pointerleave', () => h().leave())
      .on('click', (e, x) => { e.stopPropagation(); h().pin('hub', x) })
      .on('keydown', (e, x) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); h().pin('hub', x) } })
    hn.append('circle').attr('r', x => x.rad).attr('fill', '#FFFFFF').attr('stroke-width', 1.6).attr('stroke', INK)
      .attr('stroke-dasharray', x => (x.generic ? '3 2.5' : null))
    const hNum = hn.append('text').attr('class', 'dmap-hnum').attr('text-anchor', 'middle').attr('dy', '0.36em')
      .attr('font-size', x => 10 + Math.sqrt(x.n) * 1.35)
    hn.append('text').attr('class', 'dmap-hlabel').text(x => x.label).attr('text-anchor', 'middle').attr('y', x => x.rad + 15)

    const cg = svg.append('g').attr('pointer-events', 'none')
    cg.append('circle').attr('r', 74).attr('fill', '#FFFFFF').attr('opacity', 0.88)
    const cNum = cg.append('text').attr('class', 'dmap-cnum').attr('text-anchor', 'middle').attr('y', 4)
    cg.append('text').attr('class', 'dmap-csub').attr('text-anchor', 'middle').attr('y', 28)
      .text(`OF ${data.stats?.tables ?? layout.tables.length} TABLES`)
    cg.append('text').attr('class', 'dmap-csub').attr('text-anchor', 'middle').attr('y', 46).text('CONNECTED')

    const routeG = svg.append('g').attr('fill', 'none').attr('pointer-events', 'none')

    svg.on('click', () => { setPinned(null); setRouteReq(null) })
    const tNode = new Map()
    tn.each(function (t) { tNode.set(t.id, this) })
    const hNode = new Map()
    hn.each(function (x) { hNode.set(x.id, this) })
    sel.current = { svg, mem, pair, pairHit, verG, tn, dots, labels, hn, hNum, cNum, hiG, routeG, tNode, hNode, marked: [] }
    return () => {
      svg.on('click', null)
      // a route still drawing must not tick on a detached node
      svg.selectAll('*').interrupt()
    }
  }, [layout, data, nameOf])

  // ── what the link filter shows: once per filter change ──
  useEffect(() => {
    const s = sel.current
    if (!s) return
    const { memberOn, hubOn, pairOn, verOn, connected } = vis
    // many direct links would bury the ring; each one fades as there are more of them
    const pairBase = Math.max(0.16, Math.min(0.62, 0.62 * Math.sqrt(120 / Math.max(1, pairOn.size))))
    s.mem.attr('opacity', m => (memberOn.has(m.key) && hubOn.has(m.h) ? 0.42 : 0))
    s.pair.attr('opacity', p => (pairOn.has(p.key) ? pairBase : 0))
    s.pairHit.attr('pointer-events', p => (pairOn.has(p.key) ? 'stroke' : 'none'))
    s.verG.attr('display', verOn ? null : 'none')
    s.dots.attr('fill', t => (connected.has(t.id) ? DOMAIN_COLORS[t.domain] || DOMAIN_COLORS.other : '#FFFFFF'))
      .attr('stroke', t => (connected.has(t.id) ? '#FFFFFF' : HOLLOW))
    s.labels.attr('fill', t => (connected.has(t.id) ? MUTED : FAINT))
    s.hn.classed('is-off', x => !hubOn.has(x.id))
    s.hNum.text(x => vis.hubCount.get(x.id) || 0)
    s.cNum.text(connected.size)
  }, [layout, vis])

  useEffect(() => {
    const s = sel.current
    if (!s) return
    s.svg.attr('viewBox', showLabels ? '-600 -600 1200 1200' : '-440 -440 880 880')
    s.labels.attr('display', showLabels ? null : 'none')
  }, [layout, showLabels])

  // ── focus and route: dim whole layers (one class), redraw only the highlighted lines ──
  useEffect(() => {
    const s = sel.current
    if (!s) return
    s.hiG.selectAll('*').remove()
    s.routeG.selectAll('*').interrupt().remove()
    s.marked.forEach(n => n.classList.remove('hi', 'hi-self'))
    s.marked = []
    const mark = (map, id, self) => {
      const n = map.get(id)
      if (!n) return
      n.classList.add('hi')
      if (self) n.classList.add('hi-self')
      s.marked.push(n)
    }
    const line = (g, d, stroke, width, dash, opacity = 1) => g.append('path').attr('d', d).attr('stroke', stroke)
      .attr('stroke-width', width).attr('stroke-linecap', 'round').attr('stroke-dasharray', dash || null).attr('opacity', opacity)
    const verLine = (v) => { line(s.hiG, v.d, '#FFFFFF', 6); line(s.hiG, v.d, AMBER, 2.6) }
    const colorOf = id => DOMAIN_COLORS[layout.byId.get(id)?.domain] || DOMAIN_COLORS.other
    const memVis = m => vis.memberOn.has(m.key) && vis.hubOn.has(m.h)
    let active = true

    if (focus?.type === 'table' && layout.byId.has(focus.id)) {
      const id = focus.id
      const hs = new Set(layout.members.filter(m => m.t === id && memVis(m)).map(m => m.h))
      reachOf(vis, id).forEach(t => mark(s.tNode, t, t === id))
      hs.forEach(h => mark(s.hNode, h))
      layout.members.forEach(m => {
        if (hs.has(m.h) && memVis(m) && m.t !== id) line(s.hiG, m.d, colorOf(m.t), 1.1, null, 0.55)
      })
      layout.members.forEach(m => { if (m.t === id && memVis(m)) line(s.hiG, m.d, colorOf(m.t), 2.4) })
      layout.pairs.forEach(p => {
        if (vis.pairOn.has(p.key) && (p.a === id || p.b === id)) line(s.hiG, p.d, colorOf(p.a), 2.2, '5 3.5')
      })
      if (vis.verOn) layout.verified.forEach(v => { if (v.a === id || v.b === id) verLine(v) })
    } else if (focus?.type === 'hub' && layout.hubById.has(focus.id)) {
      const id = focus.id
      mark(s.hNode, id)
      layout.members.forEach(m => {
        if (m.h === id && memVis(m)) { mark(s.tNode, m.t); line(s.hiG, m.d, colorOf(m.t), 1.9, null, 0.95) }
      })
    } else if (focus?.type === 'pair' || focus?.type === 'verified') {
      const obj = (focus.type === 'pair' ? layout.pairs : layout.verified).find(p => p.key === focus.id)
      if (obj) {
        mark(s.tNode, obj.a, true)
        mark(s.tNode, obj.b, true)
        if (focus.type === 'pair') line(s.hiG, obj.d, colorOf(obj.a), 2.6, '5 3.5')
        else verLine(obj)
      } else {
        active = false
      }
    } else if (route?.result && !focus) {
      const { nodes, edges } = route.result
      nodes.forEach(n => (n.startsWith('hub:') ? mark(s.hNode, n.slice(4)) : mark(s.tNode, n, true)))
      edges.forEach((e, i) => {
        const u = nodes[i]
        let d
        let dashed = false
        if (e.m) { d = e.m.d; if (u.startsWith('hub:')) d = reversePath(d) }
        else if (e.p) { d = e.p.a === u ? e.p.d : reversePath(e.p.d); dashed = true }
        else { d = e.v.a === u ? e.v.d : reversePath(e.v.d) }
        const path = line(s.routeG, d, AMBER, 3.4)
        if (!reduce) {
          const len = path.node().getTotalLength ? path.node().getTotalLength() : 0
          if (len) {
            path.attr('stroke-dasharray', len).attr('stroke-dashoffset', len)
              .transition().delay(i * 300).duration(700).attr('stroke-dashoffset', 0)
          }
        }
        if (dashed) line(s.routeG, d, '#FFFFFF', 1.4, '2 6')
      })
    } else {
      active = false
    }
    s.svg.classed('is-focus', active)
  }, [layout, vis, focus, route, reduce])

  // ── derived text ──
  const stats = data.stats || {}
  const total = stats.tables ?? layout.tables.length
  const unlinked = useMemo(() => layout.tables.filter(t => !vis.connected.has(t.id)), [layout, vis])
  const topHubs = useMemo(() => layout.hubs.filter(h => !h.generic && vis.hubOn.has(h.id))
    .map(h => ({ ...h, shown: vis.hubCount.get(h.id) || 0 }))
    .sort((a, b) => b.shown - a.shown || a.label.localeCompare(b.label)), [layout, vis])
  const keyExamples = topHubs.slice(0, 3).map(h => h.label)
  const built = fmtDate(data.built_at)
  const loaded = fmtDate(data.data_loaded_at)
  const title = portal ? null : possessive(subject)
  const domainLabel = (id) => data.domains?.find(d => d.id === id)?.label || id

  const pinTable = useCallback((id) => {
    setRouteReq(null)
    setPinned({ type: 'table', id })
    const t = layout.byId.get(id)
    if (t && onSelectTable) onSelectTable(t)
  }, [layout, onSelectTable])
  const pinHub = useCallback((id) => { setRouteReq(null); setPinned({ type: 'hub', id }) }, [])
  const askRoute = useCallback((r) => { setPinned(null); setRouteReq(r) }, [])

  return (
    <section className="dmap" aria-label="Data map">
      <header className="dmap-head">
        <div>
          <div className="dmap-kicker">{portal ? 'Data map' : 'Context Graph · data map'}</div>
          <h2 className="dmap-title">
            How {title ? <em>{title}</em> : portal ? <em>your</em> : 'the'} data connects
          </h2>
        </div>
        <div className={`dmap-fresh${data.stale ? ' is-stale' : ''}`} role="status" title={data.stale ? W.staleTitle : undefined}>
          {data.stale && <span className="dmap-dot-warn" aria-hidden="true" />}
          <span>
            <b>{W.mapWord} built</b> {built || 'date not recorded'} · <b>data loaded</b> {loaded || 'date not recorded'}
            {data.stale && <> · <span className="dmap-stale">{W.stale}</span></>}
          </span>
        </div>
      </header>

      <p className="dmap-lede">
        {portal ? 'Every table in your data is a dot on the ring, grouped by area. ' : 'Every table is a dot on the ring, grouped by area. '}
        The <b>keys tables share</b>{keyExamples.length ? ` (${keyExamples.join(', ')}…)` : ''} sit inside, and a line means two tables
        can be joined. <b className="dmap-am">Deep amber</b> lines are {portal ? 'verified links' : 'verified joins'}; the rest
        {portal ? ' are links found in your data.' : ' were found by matching values in the data.'}
      </p>

      <div className="dmap-stage">
        <div className="dmap-viz">
          <div className="dmap-bar" role="group" aria-label="Which links to show">
            <button type="button" className="dmap-chip" aria-pressed={filter.verified}
              onClick={() => setFilter(f => ({ ...f, verified: !f.verified }))}>
              <Marker color={AMBER} /> {W.verified}
            </button>
            <button type="button" className="dmap-chip" aria-pressed={filter.matched}
              onClick={() => setFilter(f => ({ ...f, matched: !f.matched }))}>
              <Marker color={MUTED} /> {W.matched}
            </button>
            <button type="button" className="dmap-chip" aria-pressed={showLabels} onClick={() => setShowLabels(v => !v)}>
              Table names
            </button>
          </div>
          <svg ref={svgRef} className="dmap-svg" viewBox="-600 -600 1200 1200" role="img"
            aria-label="Map: tables around the ring grouped by area, shared keys inside" />
          <div className="dmap-foot">
            <div className="dmap-legend">
              <span><Marker color={MUTED} />Carries a shared key</span>
              <span><Marker color={MUTED} dashed />Direct link</span>
              <span><Marker color={AMBER} />{portal ? 'Verified link' : 'Verified join'}</span>
              <span><i className="dmap-sw dmap-sw--hollow" />No link yet</span>
              {layout.arcs.map(a => (
                <span key={a.id}><i className="dmap-sw" style={{ background: a.color }} />{a.label}</span>
              ))}
            </div>
            {layout.settings.length > 0 && (
              <div className="dmap-settings">
                <div className="dmap-eyebrow">Settings: describe the system, not people</div>
                <ul>
                  {layout.settings.map(t => (
                    <li key={t.id}>
                      <button type="button" className="dmap-set-item" title={t.why}
                        aria-pressed={pinned?.type === 'settings' && pinned.id === t.id}
                        onClick={() => setPinned({ type: 'settings', id: t.id })}>
                        <i />{portal ? t.label : t.id}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
        <Inspector
          layout={layout} vis={vis} focus={focus} W={W} portal={portal} total={total}
          nameOf={nameOf} hubLabel={hubLabel} domainLabel={domainLabel} topHubs={topHubs}
          routeReq={routeReq} route={route} setRouteReq={askRoute}
          pinnedTable={pinned?.type === 'table' ? pinned.id : null}
          onPick={pinTable}
        />
      </div>

      <Summary layout={layout} vis={vis} unlinked={unlinked} topHubs={topHubs} total={total}
        filtered={!(filter.verified && filter.matched)} nameOf={nameOf} onPick={pinTable} onHub={pinHub} />

      <div ref={tipRef} className="dmap-tip" hidden />
    </section>
  )
}

// memo: the summary changes with the link filter, not with hover
const Summary = memo(function Summary({ layout, vis, unlinked, topHubs, total, filtered, nameOf, onPick, onHub }) {
  return (
    <div className="dmap-cards">
      <div className="dmap-card">
        <div className="dmap-k">Connected</div>
        <div className="dmap-n">{vis.connected.size} <span className="dmap-of">of {total}</span></div>
        <p>tables link to at least one other table{filtered ? ' with the links shown' : ''}.</p>
        <div className="dmap-stack" role="img"
          aria-label={`${vis.connected.size} connected, ${unlinked.length} with no link, ${layout.settings.length} settings`}>
          <span style={{ flex: vis.connected.size, background: INK }} />
          <span style={{ flex: unlinked.length, background: '#E4DED2' }} />
          <span style={{ flex: layout.settings.length, background: FAINT }} />
        </div>
        <div className="dmap-stack-key">
          <span><i style={{ background: INK }} />Connected · {vis.connected.size}</span>
          <span><i style={{ background: '#E4DED2' }} />No link · {unlinked.length}</span>
          <span><i style={{ background: FAINT }} />Settings · {layout.settings.length}</span>
        </div>
      </div>
      <div className="dmap-card">
        <div className="dmap-k">Busiest keys</div>
        {topHubs.length ? (
          <ul className="dmap-bars">
            {topHubs.slice(0, 5).map(h => (
              <li key={h.id}>
                <button type="button" className="dmap-linkbtn" onClick={() => onHub(h.id)}>{h.label}</button>
                <span className="dmap-bar-track"><span style={{ width: `${(100 * h.shown) / Math.max(1, topHubs[0].shown)}%` }} /></span>
                <span className="dmap-m">{h.shown} tables</span>
              </li>
            ))}
          </ul>
        ) : <p>No key is shared by two or more tables with the links shown.</p>}
      </div>
      <div className="dmap-card">
        <div className="dmap-k">Tables with no link</div>
        <div className="dmap-n">{unlinked.length}</div>
        {unlinked.length ? (
          <UnlinkedList tables={unlinked} nameOf={nameOf} onPick={onPick} />
        ) : <p>Every table on the ring links to at least one other.</p>}
      </div>
    </div>
  )
})

function UnlinkedList({ tables, nameOf, onPick }) {
  const [all, setAll] = useState(false)
  const shown = all ? tables : tables.slice(0, 24)
  return (
    <>
      <ul className="dmap-chips">
        {shown.map(t => (
          <li key={t.id}><button type="button" className="dmap-tag" onClick={() => onPick(t.id)}>{nameOf(t.id)}</button></li>
        ))}
      </ul>
      {tables.length > shown.length && (
        <button type="button" className="dmap-linkbtn" onClick={() => setAll(true)}>Show all {tables.length}</button>
      )}
    </>
  )
}

function Inspector({ layout, vis, focus, W, portal, total, nameOf, hubLabel, domainLabel, topHubs, routeReq, route, setRouteReq, onPick, pinnedTable }) {
  let body
  if (focus?.type === 'table' && layout.byId.has(focus.id)) {
    const t = layout.byId.get(focus.id)
    const reach = reachOf(vis, t.id)
    const ms = layout.members.filter(m => m.t === t.id && vis.memberOn.has(m.key) && vis.hubOn.has(m.h))
    const ps = layout.pairs.filter(p => vis.pairOn.has(p.key) && (p.a === t.id || p.b === t.id))
    const vs = vis.verOn ? layout.verified.filter(v => v.a === t.id || v.b === t.id) : []
    const carries = Object.entries(t.carries || {})
    const on = vis.connected.has(t.id)
    body = (
      <>
        <div className="dmap-eyebrow">{domainLabel(t.domain)}</div>
        <h3>{nameOf(t.id)}</h3>
        <span className={`dmap-pill${on ? ' is-on' : ''}`}>{on ? 'Linked' : 'No link yet'}</span>
        {on ? (
          <>
            <div className="dmap-big">{reach.size - 1}</div>
            <p>other {reach.size === 2 ? 'table' : 'tables'} it links to directly or through a shared key.</p>
          </>
        ) : (
          <p>{portal ? 'Nothing in your data links to this table yet.' : 'No stored link touches this table with the links shown.'}</p>
        )}
        {ms.length > 0 && (
          <>
            <div className="dmap-eyebrow">Keys it shares</div>
            <ul>{ms.map(m => (
              <Row key={m.key} color={INK} name={hubLabel(m.h)} meta={m.cols.join(', ')}
                note={m.kinds.includes('verified') ? W.verifiedOne : null} noteVerified />
            ))}</ul>
          </>
        )}
        {ps.length > 0 && (
          <>
            <div className="dmap-eyebrow">Direct links</div>
            <ul>{ps.map(p => {
              const other = p.a === t.id ? p.b : p.a
              const cols = p.cols.map(c => (c.a === t.id ? `${c.ac} = ${c.bc}` : `${c.bc} = ${c.ac}`)).join('; ')
              return <Row key={p.key} color={DOMAIN_COLORS[layout.byId.get(other)?.domain] || MUTED} dashed
                name={nameOf(other)} meta={cols} onClick={() => onPick(other)}
                note={p.kinds.includes('verified') ? W.verifiedOne : W.matchedOne} noteVerified={p.kinds.includes('verified')} />
            })}</ul>
          </>
        )}
        {vs.length > 0 && (
          <>
            <div className="dmap-eyebrow">{portal ? 'Verified links' : 'Verified joins'}</div>
            <ul>{vs.map(v => {
              const other = v.a === t.id ? v.b : v.a
              return <Row key={v.key} color={AMBER} name={nameOf(other)} onClick={() => onPick(other)}
                meta={v.cols.map(c => (c.a === t.id ? `${c.ac} = ${c.bc}` : `${c.bc} = ${c.ac}`)).join('; ')} />
            })}</ul>
          </>
        )}
        {carries.length > 0 && (
          <>
            <div className="dmap-eyebrow">Shared keys not linked yet</div>
            <ul>{carries.map(([h, cols]) => (
              <Row key={h} color={HOLLOW} dashed name={keyName(layout.hubById.get(h)?.label || h.replace(/_/g, ' '))} meta={cols.join(', ')} />
            ))}</ul>
          </>
        )}
      </>
    )
  } else if (focus?.type === 'hub' && layout.hubById.has(focus.id)) {
    const h = layout.hubById.get(focus.id)
    const ms = layout.members.filter(m => m.h === h.id && vis.memberOn.has(m.key))
    body = (
      <>
        <div className="dmap-eyebrow">Shared key</div>
        <h3>{h.label}</h3>
        <div className="dmap-big">{ms.length}</div>
        <p>{h.generic
          ? 'lookup tables share a plain "code" column. That alone does not say what each one decodes; the direct links do.'
          : 'tables carry this key, so any two of them can be joined on it.'}</p>
        <ul>{ms.map(m => (
          <Row key={m.key} color={DOMAIN_COLORS[layout.byId.get(m.t)?.domain] || MUTED} name={nameOf(m.t)}
            meta={m.cols.join(', ')} onClick={() => onPick(m.t)}
            note={m.kinds.includes('verified') ? W.verifiedOne : null} noteVerified />
        ))}</ul>
      </>
    )
  } else if ((focus?.type === 'pair' || focus?.type === 'verified')) {
    const obj = (focus.type === 'pair' ? layout.pairs : layout.verified).find(p => p.key === focus.id)
    if (obj) {
      const verifiedLink = focus.type === 'verified' || obj.kinds?.includes('verified')
      body = (
        <>
          <div className="dmap-eyebrow">{verifiedLink ? (portal ? 'Verified link' : 'Verified join') : `Direct link · ${W.matchedOne}`}</div>
          <h3>{nameOf(obj.a)} ↔ {nameOf(obj.b)}</h3>
          <ul>{obj.cols.map((c, i) => (
            <Row key={i} color={verifiedLink ? AMBER : MUTED} dashed={!verifiedLink}
              name={c.a === obj.a ? `${c.ac} = ${c.bc}` : `${c.bc} = ${c.ac}`}
              meta={!portal && c.method ? c.method : ''} />
          ))}</ul>
        </>
      )
    }
  } else if (focus?.type === 'settings') {
    const t = layout.settings.find(s => s.id === focus.id)
    if (t) {
      body = (
        <>
          <div className="dmap-eyebrow">Settings</div>
          <h3>{nameOf(t.id)}</h3>
          <span className="dmap-pill">Stands alone by design</span>
          <p>{t.why} It describes how the system is set up, not a person, a job or a pay run, so there is nothing to link it to.</p>
        </>
      )
    }
  }
  if (!body) {
    body = (
      <>
        <div className="dmap-eyebrow">Connected</div>
        <div className="dmap-big">{vis.connected.size}<span className="dmap-of"> / {total}</span></div>
        <p>tables link to at least one other table. Hollow dots have no link yet; settings tables sit below the map.</p>
        {topHubs.length > 0 && (
          <>
            <div className="dmap-eyebrow">Busiest keys</div>
            <ul>{topHubs.slice(0, 7).map(h => (
              <Row key={h.id} color={INK} name={keyName(h.label)} meta={`${h.shown} tables`} />
            ))}</ul>
          </>
        )}
        <p className="dmap-hint">Hover the ring to explore; click a table, key or dashed link to pin it.</p>
      </>
    )
  }
  return (
    <aside className="dmap-inspector" aria-live="polite">
      {body}
      <RouteFinder layout={layout} nameOf={nameOf} hubLabel={hubLabel} routeReq={routeReq} route={route}
        setRouteReq={setRouteReq} defaultFrom={pinnedTable} />
    </aside>
  )
}

// memo: the two pickers hold every table, and hovering the ring must not rebuild them
const RouteFinder = memo(function RouteFinder({ layout, nameOf, hubLabel, routeReq, route, setRouteReq, defaultFrom }) {
  const ids = useMemo(() => layout.tables.map(t => t.id).sort((a, b) => nameOf(a).localeCompare(nameOf(b))), [layout, nameOf])
  const [from, setFrom] = useState(() => routeReq?.from || ids[0] || '')
  const [to, setTo] = useState(() => routeReq?.to || ids[ids.length - 1] || '')
  const [prevDefault, setPrevDefault] = useState(defaultFrom)
  if (defaultFrom !== prevDefault) {
    setPrevDefault(defaultFrom)
    if (defaultFrom) setFrom(defaultFrom)
  }
  let out = null
  if (route && route.from === from && route.to === to) {
    if (from === to) {
      out = <div className="dmap-route is-none">Pick two different tables.</div>
    } else if (route.result) {
      out = (
        <div className="dmap-route">
          {route.result.nodes.map((x, i) => (
            <span key={`${x}-${i}`}>
              {i > 0 && ' → '}
              {x.startsWith('hub:') ? <b>{hubLabel(x.slice(4))}</b> : <span>{nameOf(x)}</span>}
            </span>
          ))}
        </div>
      )
    } else {
      out = <div className="dmap-route is-none">No route between these two tables with the links shown.</div>
    }
  }
  return (
    <div className="dmap-pf">
      <div className="dmap-eyebrow">Route between two tables</div>
      <select aria-label="From table" value={from} onChange={e => setFrom(e.target.value)}>
        {ids.map(x => <option key={x} value={x}>{nameOf(x)}</option>)}
      </select>
      <select aria-label="To table" value={to} onChange={e => setTo(e.target.value)}>
        {ids.map(x => <option key={x} value={x}>{nameOf(x)}</option>)}
      </select>
      <button type="button" className="dmap-btn" onClick={(e) => { e.stopPropagation(); setRouteReq({ from, to }) }}>
        Draw the route
      </button>
      {out}
    </div>
  )
})
